import { trigger, state, style, transition, animate } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { faMessageExclamation } from '@fortawesome/pro-solid-svg-icons';

import { Subscription } from 'rxjs';
import {
  SurveyProgress,
  SourceVariables,
  SurveyFeedback,
  QuestionType,
  SurveyResult,
  ChoiceQuestionTypes,
  CarouselType,
  MultiChoiceQuestionTypes
} from '@empathy/survey/base';
import { SurveyModel, PageModel, Question } from '@empathy/survey-core';

import { SurveyViewerStateService } from '../../services/survey-viewer-state.service';
import { SurveyViewerService } from '../../services/survey-viewer.service';

@Component({
  selector: 'empathy-dep-viewer',
  templateUrl: 'viewer.component.html',
  styleUrls: ['./viewer.component.scss', './_viewer-theme.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('openingAnimation', [state('void', style({ opacity: 0 })), transition(':enter', [animate('300ms ease-in')])])]
})
export class ViewerComponent implements OnInit, OnDestroy {
  progressSaved = input<boolean>(false);
  submitting = input<boolean>(false);
  surveyModel = input.required<SurveyModel>();
  surveyResultResume = input<SurveyResult>();
  testMode = input<boolean>(false);
  variables = input<SourceVariables>({});

  completed = output();
  feedback = output<SurveyFeedback>();
  httpError = output();
  invalidVariables = output();
  progress = output<SurveyProgress>();
  started = output();
  completedSurvey = output<{ results: SurveyResult; autocomplete: boolean }>();

  activePage$ = computed(() => this.surveyModel().pages[this.surveyViewerService.activePageIndex$()]);
  autoSubmitting$ = signal(false);
  questionCtrls$ = computed(() => this.surveyViewerService.pageControls[this.surveyModel().pages[this.surveyViewerService.activePageIndex$()].id]);
  isPreparing$ = computed(() => this.surveyViewerService.activePageIndex$() === null);
  isSaving$ = signal(false);
  showCompleteButton$ = computed(() => this.surveyViewerService.activePageIndex$() === this.surveyModel().pages.length - 1);
  showEndPage$ = signal(false);
  showFeedbackPage$ = signal(false);
  showWelcomePage$ = computed(() => this.surveyViewerService.activePageIndex$() === -1);
  terminatedBy$ = signal<string>('');
  progressValue$ = computed(() => ((this.surveyViewerService.activePageIndex$() + 1) / this.surveyModel()?.pages.length) * 100);

  faMessageExclamation = faMessageExclamation;

  private surveyViewerService = inject(SurveyViewerService);
  private surveyViewerStateService = inject(SurveyViewerStateService);

  private subscription: Subscription;
  private calculatedNextPageIndex = -1;

  constructor() {
    effect(
      () => {
        if (this.surveyModel()) {
          this.surveyViewerService.initForm(this.surveyModel(), this.variables());

          if (this.surveyResultResume()) {
            this.surveyViewerService.resumeSurvey(this.surveyResultResume());
          }
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (this.progressSaved()) {
          this.surveyViewerService.activePageIndex$.set(this.calculatedNextPageIndex + 1);
          this.isSaving$.set(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const page = this.activePage$();

        if (page) {
          const skipped = this.skipQuestionIfIsAutopopulatedCandidate(page);
          if (!skipped) {
            this.surveyViewerStateService.setActivePage(page);
          }
        }
      },
      { allowSignalWrites: true }
    );
  }

  private get inLastPage(): boolean {
    return this.calculatedNextPageIndex === this.surveyModel().pages.length - 1;
  }

  ngOnInit(): void {
    /// TODO: EQ-1051
    if (this.variables()) {
      const questionKeys = Object.keys(this.surveyViewerService.questions);
      for (const fieldName of questionKeys) {
        if (fieldName.startsWith('SV')) {
          const question = this.surveyViewerService.questions[fieldName];

          if (question.variableToken && this.variables()[question.variableToken] && question.type === QuestionType.SingleChoice) {
            const valueVariable = this.variables()[question.variableToken];

            if (valueVariable && !question.options.find((o) => o.text === valueVariable)) {
              this.invalidVariables.emit();
            }
          }
        }
      }
    }
    // END PIECE OF CODE

    this.subscription = this.surveyViewerService.forceExit$.subscribe((questionId) => this.progress.emit(this.mapTerminated(questionId)));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.surveyViewerService.destroy();
  }

  onPrev(): void {
    this.calculatedNextPageIndex = this.surveyViewerService.activePageIndex$();

    while (this.surveyModel().pages[this.calculatedNextPageIndex - 1].visibleQuestions.length === 0) {
      this.calculatedNextPageIndex--;
    }

    this.surveyViewerService.activePageIndex$.set(this.calculatedNextPageIndex - 1);
  }

  onNext(): void {
    if (this.surveyViewerService.activePageIndex$() === -1) {
      this.started.emit();
      this.surveyViewerService.activePageIndex$.set(0);
      return;
    }

    this.calculatedNextPageIndex = this.surveyViewerService.activePageIndex$();

    while (!this.inLastPage && this.surveyModel().pages[this.calculatedNextPageIndex + 1].visibleQuestions.length === 0) {
      this.calculatedNextPageIndex++;
    }

    if (this.inLastPage) {
      this.endSurvey();
      return;
    }

    this.isSaving$.set(true);
    this.progress.emit(this.mapSurveyProgress(this.surveyViewerService.activePageIndex$()));
  }

  onSubmitFeedback(feedback: SurveyFeedback): void {
    this.feedback.emit(feedback);
    if (this.surveyModel().useEndPageInd) {
      this.showFeedbackPage$.set(false);
      this.showEndPage$.set(true);
    } else {
      this.completed.emit();
    }
  }

  private mapTerminated(questionId: string): SurveyProgress {
    this.showEndPage$.set(this.surveyModel().useEndPageInd);
    this.terminatedBy$.set(questionId);
    return new SurveyProgress(
      JSON.stringify(this.surveyViewerService.mapAnswers()),
      this.surveyViewerService
        .mapMetadata(this.surveyModel().pages[this.surveyViewerService.activePageIndex$()].id, questionId)
        .map((metadata) => metadata.questionId),
      questionId
    );
  }

  private mapSurveyProgress(pageIndex: number): SurveyProgress {
    return new SurveyProgress(
      JSON.stringify(this.surveyViewerService.mapAnswers()),
      this.surveyViewerService.mapMetadata(this.surveyModel().pages[pageIndex].id).map((metadata) => metadata.questionId)
    );
  }

  private endSurvey(): void {
    if (this.surveyModel().settings?.useFeedbackInd) {
      this.showFeedbackPage$.set(true);
    } else {
      this.showEndPage$.set(this.surveyModel().useEndPageInd);
    }

    this.autoSubmitting$.set(!this.surveyModel().useEndPageInd && !this.surveyModel().settings?.useFeedbackInd);

    this.completedSurvey.emit({
      results: this.surveyViewerService.mapAnswers(),
      autocomplete: !this.surveyModel().useEndPageInd && !this.surveyModel().settings?.useFeedbackInd
    });
  }

  private skipQuestionIfIsAutopopulatedCandidate(page: PageModel): boolean {
    const areAllQuestionsAutoPopulated: { [qId: string]: boolean } = {};
    for (const question of page.visibleQuestions) {
      if (!question.optionsSettings?.autopopulateInd || !question.requiredInd) {
        areAllQuestionsAutoPopulated[question.id] = false;
        continue;
      }

      const qControl = this.surveyViewerService.questionControls[question.id];

      const canQuestionBeAutopopulated = qControl && this.isControlValueEmpty(qControl.value.value) && question.visibleOptions.length === 1;
      const canCarouselBeAutopopulated = qControl && this.isCarouselControlValueEmpty(qControl.value) && question.visibleOptions.length === 1;

      if (ChoiceQuestionTypes.includes(question.type) && !CarouselType.includes(question.type) && canQuestionBeAutopopulated) {
        this.autopopulateOptionsQuestion(qControl, question);
        areAllQuestionsAutoPopulated[question.id] = true;
        continue;
      }

      if (ChoiceQuestionTypes.includes(question.type) && CarouselType.includes(question.type) && canCarouselBeAutopopulated) {
        this.autopopulateCardsQuestion(qControl, question);
        areAllQuestionsAutoPopulated[question.id] = true;
        continue;
      }

      areAllQuestionsAutoPopulated[question.id] = false;
    }

    if (Object.values(areAllQuestionsAutoPopulated).length && Object.values(areAllQuestionsAutoPopulated).every((v) => v)) {
      this.onNext();
      return true;
    }

    return false;
  }

  private isControlValueEmpty(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  private autopopulateOptionsQuestion(qControl: AbstractControl, question: Question): void {
    qControl.setValue({
      value: MultiChoiceQuestionTypes.includes(question.type) ? [question.visibleOptions[0].value] : question.visibleOptions[0].value,
      otherText: ''
    });
    qControl.markAsDirty();
  }

  private autopopulateCardsQuestion(qControl: AbstractControl, question: Question): void {
    const cards = question.cards;
    cards.forEach((card) => {
      (qControl as FormGroup).controls[card.id].setValue({
        value: MultiChoiceQuestionTypes.includes(question.type) ? [question.visibleOptions[0].value] : question.visibleOptions[0].value,
        otherText: ''
      });
      (qControl as FormGroup).controls[card.id].markAsDirty();
      return;
    });
    qControl.markAsDirty();
  }

  private isCarouselControlValueEmpty(value: any): boolean {
    if (!value || typeof value !== 'object') {
      return true;
    }

    return Object.values(value).every(
      (cardValue: { value: string; otherText: string }) => !cardValue || cardValue.value === null || cardValue.value === undefined || cardValue.value === ''
    );
  }
}
